// Missions terrain: pure helpers shared by the list, the form and the mission page. Unit-tested in missions.test.js.
// The server decides every right and rule; these only label, format and prepare requests.
import { hasPermission } from "./permissions";
import { errorMessage, isVersionConflict } from "./api";

export const MISSION_READ_KEYS = ["missions:view", "missions:view_all", "missions:manage", "missions:manage_all"];
export const MISSION_WRITE_KEYS = ["missions:manage", "missions:manage_all"];
export const DEFAULT_TIMEZONE = "Africa/Dakar";

export const STATUS_META = {
  draft: { label: "Brouillon", className: "bg-slate-100 text-slate-700 border-slate-200" },
  planned: { label: "Planifiée", className: "bg-[#E5F0F5] text-[#083346] border-[#C9DFE8]" },
  in_progress: { label: "En cours", className: "bg-teal-50 text-teal-800 border-teal-200" },
  completed: { label: "Terminée", className: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  postponed: { label: "Reportée", className: "bg-amber-50 text-amber-800 border-amber-200" },
  cancelled: { label: "Annulée", className: "bg-slate-50 text-slate-500 border-slate-200" },
  interrupted: { label: "Interrompue", className: "bg-red-50 text-red-700 border-red-200" },
};
export const STATUS_FILTERS = ["draft", "planned", "in_progress", "postponed", "completed", "interrupted", "cancelled"];

export const ROLE_LABELS = { lead: "Responsable", member: "Intervenant", trainer: "Formateur", technician: "Technicien" };
export const ADDABLE_ROLES = ["member", "trainer", "technician"];
export const PRESENCE_LABELS = { full: "Toute la mission", partial: "Partielle" };

// Lifecycle actions, in the order the mission page shows them. `reason`: a reason is required; `dates`: new dates are required.
export const ACTIONS = {
  plan: { done: "Mission planifiée", label: "Planifier", title: "Planifier la mission", primary: true },
  start: { done: "Mission démarrée", label: "Démarrer", title: "Démarrer la mission", primary: true },
  finish: { done: "Mission terminée", label: "Terminer", title: "Terminer la mission", primary: true },
  postpone: { done: "Mission reportée", label: "Reporter", title: "Reporter la mission", reason: true, dates: true },
  interrupt: { done: "Mission interrompue", label: "Interrompre", title: "Interrompre la mission", reason: true, danger: true },
  cancel: { done: "Mission annulée", label: "Annuler la mission", title: "Annuler la mission", reason: true, danger: true },
};
export const ACTION_ORDER = ["plan", "start", "finish", "postpone", "interrupt", "cancel"];

export const ACTION_HELP = {
  plan: "La mission entre dans l'agenda de l'équipe. Les compétences et les chevauchements sont vérifiés.",
  start: "La mission passe « en cours ». L'heure de démarrage est enregistrée.",
  finish: "La mission passe « terminée ». Elle ne sera plus modifiable.",
  postpone: "Indiquez les nouvelles dates et le motif. La mission devra être planifiée de nouveau.",
  interrupt: "La mission s'arrête avant sa fin. Elle ne sera plus modifiable.",
  cancel: "La mission est annulée. Elle reste dans l'historique mais ne sera plus modifiable.",
};

export const HISTORY_LABELS = {
  created: "Mission créée", updated: "Mission modifiée", plan: "Mission planifiée", start: "Mission démarrée", finish: "Mission terminée",
  postpone: "Mission reportée", cancel: "Mission annulée", interrupt: "Mission interrompue", member_added: "Membre ajouté",
  member_removed: "Membre retiré", lead_changed: "Responsable changé",
  report_started: "Compte rendu commencé", report_submitted: "Compte rendu soumis à validation", report_published: "Compte rendu publié",
  report_approved: "Compte rendu validé", report_returned: "Compte rendu renvoyé pour correction", report_reopened: "Compte rendu rouvert",
};

export const MIN_REASON = 3;

export function canSeeMissions(user) {
  return !!user?.features?.missions && hasPermission(user, MISSION_READ_KEYS);
}

export function canCreateMission(user) {
  return !!user?.features?.missions && hasPermission(user, MISSION_WRITE_KEYS);
}

export function statusLabel(status) {
  return STATUS_META[status]?.label || status;
}

// --- Dates: stored in UTC, shown and entered in the mission's time zone ----------------------------------------------------
function zoneParts(date, timeZone) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(date);
  const get = (type) => Number(parts.find((p) => p.type === type).value);
  return { year: get("year"), month: get("month"), day: get("day"), hour: get("hour"), minute: get("minute"), second: get("second") };
}

function zoneOffsetMs(date, timeZone) {
  const p = zoneParts(date, timeZone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(date.getTime() / 1000) * 1000;
}

// "2026-10-20T09:30" read in `timeZone` -> "2026-10-20T09:30:00.000Z" (null when empty or invalid).
export function localInputToIso(value, timeZone = DEFAULT_TIMEZONE) {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value || "");
  if (!match) return null;
  const [, y, mo, d, h, mi] = match.map(Number);
  const wall = Date.UTC(y, mo - 1, d, h, mi);
  let instant = wall - zoneOffsetMs(new Date(wall), timeZone);
  instant = wall - zoneOffsetMs(new Date(instant), timeZone); // second pass: right offset around a DST change
  return new Date(instant).toISOString();
}

// ISO instant -> "2026-10-20T09:30" in `timeZone`, for a datetime-local input.
export function isoToLocalInput(iso, timeZone = DEFAULT_TIMEZONE) {
  if (!iso) return "";
  const p = zoneParts(new Date(iso), timeZone);
  const pad = (n) => String(n).padStart(2, "0");
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

export function formatDateTime(iso, timeZone = DEFAULT_TIMEZONE) {
  if (!iso) return "Non planifiée";
  return new Intl.DateTimeFormat("fr-FR", { timeZone, day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
}

// "lun. 20/10 · 09:00 → 12:30" on one day, "20/10 09:00 → 22/10 17:00" across days.
export function formatPeriod(startIso, endIso, timeZone = DEFAULT_TIMEZONE) {
  if (!startIso || !endIso) return "Non planifiée";
  const day = (iso) => new Intl.DateTimeFormat("fr-FR", { timeZone, day: "2-digit", month: "2-digit" }).format(new Date(iso));
  const time = (iso) => new Intl.DateTimeFormat("fr-FR", { timeZone, hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
  const weekday = new Intl.DateTimeFormat("fr-FR", { timeZone, weekday: "short" }).format(new Date(startIso));
  if (day(startIso) === day(endIso)) return `${weekday} ${day(startIso)} · ${time(startIso)} → ${time(endIso)}`;
  return `${day(startIso)} ${time(startIso)} → ${day(endIso)} ${time(endIso)}`;
}

// The form's period check, mirroring the server (end after start, at most 31 days). Returns an error text or "".
const MAX_MISSION_DAYS = 31;
export function periodError(startIso, endIso) {
  if (!startIso || !endIso) return "Indiquez le début et la fin.";
  const span = new Date(endIso) - new Date(startIso);
  if (span <= 0) return "La fin doit être après le début.";
  if (span > MAX_MISSION_DAYS * 24 * 3600 * 1000) return `Une mission ne dure pas plus de ${MAX_MISSION_DAYS} jours : découpez-la.`;
  return "";
}

// Default end of a new mission: start + the type's indicative duration (2 h without one).
export function defaultEnd(startLocal, durationMinutes, timeZone = DEFAULT_TIMEZONE) {
  const startIso = localInputToIso(startLocal, timeZone);
  if (!startIso) return "";
  const minutes = durationMinutes || 120;
  return isoToLocalInput(new Date(new Date(startIso).getTime() + minutes * 60000).toISOString(), timeZone);
}

// --- Warnings (409 `warnings`, never blocking: they are confirmed) ---------------------------------------------------------
export function warningsFrom(error) {
  const detail = error?.response?.data?.detail;
  if (error?.response?.status === 409 && detail?.code === "warnings" && Array.isArray(detail.warnings)) return detail.warnings;
  return null;
}

export function warningKinds(warnings) {
  return [...new Set((warnings || []).map((w) => w.kind))];
}

export function describeWarning(w, levelLabel = (code) => code) {
  const who = w.member?.name || "Cette personne";
  if (w.kind === "skill_missing") {
    const skills = (w.skills || []).map((s) => `${s.label || "compétence"} (${levelLabel(s.min_level)})`).join(", ");
    return `${who} n'a pas, le jour de la mission, la compétence ou l'habilitation attendue : ${skills}.`;
  }
  if (w.kind === "overlap") {
    const refs = (w.missions || []).filter(Boolean);
    const hidden = (w.missions || []).length - refs.length;
    const parts = [...refs, ...(hidden ? [`${hidden} mission${hidden > 1 ? "s" : ""} que vous ne voyez pas`] : [])];
    return `${who} est déjà sur une autre mission aux mêmes heures : ${parts.join(", ")}.`;
  }
  return `${who} : avertissement.`;
}

// --- Requests -------------------------------------------------------------------------------------------------------------
export function buildListParams({ tab, status, typeId, search, skip = 0, limit = 50 }) {
  const params = { skip, limit };
  if (tab === "mine") params.mine = true;
  if (status) params.status = status;
  if (typeId) params.type_id = typeId;
  if (search && search.trim()) params.search = search.trim();
  return params;
}

export function missionErrorMessage(error, fallback = "Une erreur est survenue. Réessayez.") {
  if (!error?.response) return "Connexion impossible. Vérifiez votre réseau et réessayez.";
  if (isVersionConflict(error)) return "La mission vient d'être modifiée par quelqu'un d'autre. Elle a été rechargée : vérifiez puis recommencez.";
  return errorMessage(error, fallback);
}


export function typeChoices(types, currentId) {
  return (types || []).filter((t) => t.active || t.id === currentId);
}

// --- Planning: one week, one row per person, chevauchements flagged ---------------------------------------------------------
const SCHEDULED_STATUSES = ["planned", "in_progress"];

function addDays(day, n) {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// "YYYY-MM-DD" of an instant in `timeZone`.
export function dayIn(iso, timeZone = DEFAULT_TIMEZONE) {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
}

// The Monday → Sunday of the week holding `day`.
export function weekOf(day) {
  const weekday = (new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7; // Monday = 0
  const monday = addDays(day, -weekday);
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

export function shiftWeek(days, weeks) {
  return weekOf(addDays(days[0], 7 * weeks));
}

export function formatWeek(days) {
  const fmt = (day, opts) => new Intl.DateTimeFormat("fr-FR", { timeZone: "UTC", ...opts }).format(new Date(`${day}T00:00:00Z`));
  return `${fmt(days[0], { day: "numeric", month: "short" })} – ${fmt(days[6], { day: "numeric", month: "short", year: "numeric" })}`;
}

export function formatDayHeader(day) {
  return new Intl.DateTimeFormat("fr-FR", { timeZone: "UTC", weekday: "short", day: "2-digit", month: "2-digit" }).format(new Date(`${day}T00:00:00Z`));
}

// The days of `days` a mission covers, in its own time zone (an end at 00:00 does not take the next day).
export function missionDays(mission, days) {
  const first = dayIn(mission.planned_start_at, mission.timezone);
  const last = dayIn(new Date(new Date(mission.planned_end_at).getTime() - 1).toISOString(), mission.timezone);
  return days.filter((d) => d >= first && d <= last);
}

// Ids of the missions of one person that overlap another of their scheduled missions (same rule as the server: strict overlap).
export function overlappingIds(missions) {
  const scheduled = missions.filter((m) => SCHEDULED_STATUSES.includes(m.status));
  const out = new Set();
  for (let i = 0; i < scheduled.length; i += 1) {
    for (let k = i + 1; k < scheduled.length; k += 1) {
      const a = scheduled[i];
      const b = scheduled[k];
      if (new Date(a.planned_start_at) < new Date(b.planned_end_at) && new Date(b.planned_start_at) < new Date(a.planned_end_at)) {
        out.add(a.id);
        out.add(b.id);
      }
    }
  }
  return out;
}

// Rows of the week view: one per person of the teams (accounts and fiches), plus « Sans équipe ». Each cell = the missions of that day.
export function planningRows(items, days) {
  const rows = new Map();
  const row = (key, name, kind) => {
    if (!rows.has(key)) rows.set(key, { key, name, kind, missions: [] });
    return rows.get(key);
  };
  for (const m of items) {
    if (!m.team?.length) row("none", "Sans équipe", "none").missions.push(m);
    for (const a of m.team || []) row(`${a.member.kind}:${a.member.id}`, a.member.name, a.member.kind).missions.push(m);
  }
  return [...rows.values()]
    .sort((a, b) => (a.kind === "none") - (b.kind === "none") || a.name.localeCompare(b.name, "fr"))
    .map((r) => {
      const clashes = overlappingIds(r.missions);
      const cells = Object.fromEntries(days.map((d) => [d, r.missions.filter((m) => missionDays(m, [d]).length)
        .sort((a, b) => a.planned_start_at.localeCompare(b.planned_start_at))]));
      return { ...r, cells, clashes };
    });
}

// Mobile: the week as days, each with its missions (a multi-day mission appears on each of its days).
export function planningByDay(items, days) {
  return days.map((day) => ({ day, missions: items.filter((m) => missionDays(m, [day]).length).sort((a, b) => a.planned_start_at.localeCompare(b.planned_start_at)) }));
}

export function formatTime(iso, timeZone = DEFAULT_TIMEZONE) {
  return new Intl.DateTimeFormat("fr-FR", { timeZone, hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
}
